import { useMemo } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { javascript } from '@codemirror/lang-javascript'
import { EditorView } from '@codemirror/view'
import { Check, Circle, Code2, RotateCcw } from 'lucide-react'

interface CodeSurfaceProps {
  source: string
  canonicalSource: string
  error: string | null
  onChange: (source: string) => void
  onApply: () => void
  onFormat: () => void
}

export function CodeSurface({
  source,
  canonicalSource,
  error,
  onChange,
  onApply,
  onFormat,
}: CodeSurfaceProps) {
  const dirty = source !== canonicalSource
  const extensions = useMemo(
    () => [javascript({ jsx: true, typescript: true }), EditorView.lineWrapping],
    [],
  )

  return (
    <section className="code-surface" aria-label="Code editor">
      <div className="code-tabs">
        <button className="code-tab is-active" type="button">
          <Code2 size={13} /> App.tsx
          {dirty ? <Circle className="dirty-dot" size={7} fill="currentColor" /> : null}
        </button>
        <span className="code-tab" aria-label="Tokens file">
          # tokens.css
        </span>
      </div>
      <div className="code-actions">
        <span className={`sync-state ${dirty ? 'is-dirty' : ''}`}>
          {dirty ? 'Draft diverged' : 'Graph reconciled'}
        </span>
        {dirty ? (
          <button className="quiet-button" type="button" onClick={onFormat}>
            <RotateCcw size={12} /> Revert
          </button>
        ) : null}
        <button
          className="apply-button"
          type="button"
          disabled={!dirty}
          onClick={onApply}
        >
          <Check size={12} /> Apply
          <kbd>⌘S</kbd>
        </button>
      </div>
      <div className="editor-wrap">
        <CodeMirror
          value={source}
          height="100%"
          extensions={extensions}
          onChange={onChange}
          basicSetup={{
            foldGutter: false,
            highlightActiveLineGutter: true,
            autocompletion: true,
            bracketMatching: true,
            closeBrackets: true,
          }}
          aria-label="App.tsx source editor"
        />
      </div>
      {error ? (
        <div className="code-diagnostic" role="alert">
          <span>AST guard</span>
          <p>{error}</p>
        </div>
      ) : null}
    </section>
  )
}
